import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronRight } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { SupplementLineItemCard } from './SupplementLineItemCard';
import type { DisplayLineItem } from '@/types/supplement';

import { LucideIcon } from 'lucide-react';

interface CategorySectionProps {
  title: string;
  items: DisplayLineItem[];
  supplementId: number;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  colorClass: string; // e.g., 'red', 'orange', 'blue', 'purple', 'green'
  icon: LucideIcon;
}

/**
 * Reusable category section component for supplement room cards
 * Displays a collapsible card with category header and line items
 */
export function CategorySection({
  title,
  items,
  supplementId,
  isOpen,
  onOpenChange,
  colorClass,
  icon: Icon,
}: CategorySectionProps) {
  if (items.length === 0) {
    return null;
  }

  const textColorClass = `text-${colorClass}-600 dark:text-${colorClass}-400`;

  return (
    <Card className="overflow-hidden">
      <Collapsible open={isOpen} onOpenChange={onOpenChange}>
        <CollapsibleTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-auto p-3 hover:bg-muted/50 gap-2 w-full justify-start rounded-none"
          >
            <ChevronRight
              className={cn(
                'h-4 w-4 transition-transform duration-200',
                textColorClass,
                isOpen && 'rotate-90'
              )}
            />
            <Icon className={cn('h-4 w-4', textColorClass)} />
            <h4 className={cn('text-sm font-semibold', textColorClass)}>
              {title} ({items.length})
            </h4>
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="p-3 pt-0 space-y-2">
            {items.map((item, index) => (
              <SupplementLineItemCard
                key={`${item.category}-${index}`}
                item={item}
                supplementId={supplementId}
              />
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}
